import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { hikingPath, hikingUrl, hikingCompleteProfilePath } from '../utils/paths';
import PhotoGallery from './PhotoGallery';
import { formatScheduleDateTime } from '../utils/scheduleTimeZone';
import {
  formatDistance,
  formatDuration,
  formatElevation,
  formatOutingPace,
  formatTrailDifficulty,
  formatTrailRouteShape,
  formatTrailTerrain,
} from '../config/hikerProfileOptions';
import './HikingOutingDetail.css';

const STATUS_OPTIONS = [
  { value: 'AVAILABLE', label: 'Going' },
  { value: 'MAYBE', label: 'Maybe' },
  { value: 'RESERVE', label: 'Reserve' },
  { value: 'UNAVAILABLE', label: 'Cannot make it' },
];

const HikingOutingDetail = () => {
  const { id } = useParams();
  const { user, isAuthenticated, isAdmin } = useAuth();
  const [outing, setOuting] = useState(null);
  const [votes, setVotes] = useState([]);
  const [hiker, setHiker] = useState(null);
  const [myStatus, setMyStatus] = useState('AVAILABLE');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [voting, setVoting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [shareUrl, setShareUrl] = useState('');
  const [canManage, setCanManage] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const outingData = await api.getHikingOuting(id);
      setOuting(outingData);
      if (outingData.shareUrl) {
        setShareUrl(outingData.shareUrl);
      } else if (outingData.shareToken) {
        setShareUrl(hikingUrl(`/public/outings/${outingData.shareToken}`));
      }

      try {
        const club = await api.getClubById(outingData.clubId);
        setCanManage(!!(user?.id && (club.adminUserId === user.id || isAdmin())));
      } catch {
        setCanManage(false);
      }

      const availability = await api.getHikingOutingAvailability(id, user?.id);
      setVotes(Array.isArray(availability) ? availability : []);

      if (isAuthenticated && user?.id) {
        let membershipPlayerId = null;
        try {
          const profile = await api.getMyHikerProfile(user.id);
          membershipPlayerId = profile?.membershipPlayerId;
          if (membershipPlayerId) {
            setHiker({ id: membershipPlayerId, name: profile?.name || user?.name });
          }
        } catch {
          // No hiking profile yet
        }

        if (!membershipPlayerId) {
          const players = await api.getAllPlayers(user.id);
          const me = (players || []).find((p) => p.userId === user.id) || null;
          setHiker(me);
          membershipPlayerId = me?.id;
        }

        if (membershipPlayerId) {
          const mine = (availability || []).find((v) => v.playerId === membershipPlayerId);
          if (mine) {
            setMyStatus(mine.status);
            setNotes(mine.notes || '');
          }
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to load outing');
    } finally {
      setLoading(false);
    }
  }, [id, user, isAuthenticated, isAdmin]);

  useEffect(() => {
    load();
  }, [load]);

  const handleVote = async (e) => {
    e.preventDefault();
    if (!hiker?.id) {
      setError('Complete your hiking profile to vote on availability.');
      return;
    }
    try {
      setVoting(true);
      setError('');
      setSuccess('');
      await api.voteHikingOutingAvailability(
        id,
        hiker.id,
        { status: myStatus, notes: notes.trim() || null },
        user.id
      );
      setSuccess('Availability saved');
      await load();
    } catch (err) {
      setError(err.message || 'Failed to save vote');
    } finally {
      setVoting(false);
    }
  };

  const handleShare = async () => {
    try {
      setError('');
      const result = await api.shareHikingOuting(id, user.id);
      const url = result.shareUrl || hikingUrl(`/public/outings/${result.shareToken}`);
      setShareUrl(url);
      setOuting((prev) => (prev ? { ...prev, shareToken: result.shareToken, shareUrl: url } : prev));
      setSuccess('Share link ready');
    } catch (err) {
      setError(err.message || 'Failed to create share link');
    }
  };

  const copyShare = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setSuccess('Link copied');
    } catch {
      setError('Could not copy link');
    }
  };

  const handlePhotos = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    try {
      await api.uploadHikingOutingPhotos(id, files, user.id);
      setSuccess('Photos uploaded');
      await load();
    } catch (err) {
      setError(err.message || 'Failed to upload photos');
    }
  };

  const handleDeletePhoto = async (photoUrl) => {
    try {
      await api.deleteHikingOutingPhoto(id, photoUrl, user.id);
      await load();
    } catch (err) {
      setError(err.message || 'Failed to delete photo');
    }
  };

  if (loading) return <div className="loading">Loading outing...</div>;
  if (!outing) return <div className="error-message">{error || 'Outing not found'}</div>;

  const counts = votes.reduce(
    (acc, vote) => {
      acc[vote.status] = (acc[vote.status] || 0) + 1;
      return acc;
    },
    { AVAILABLE: 0, MAYBE: 0, RESERVE: 0, UNAVAILABLE: 0 }
  );

  const whatsappHref = shareUrl
    ? api.shareHikingOutingViaWhatsApp({
        title: outing.title,
        outingDate: outing.outingDate,
        meetingLocation: outing.meetingLocation,
        trailTitle: outing.trail?.title,
        shareUrl,
      })
    : null;

  return (
    <div className="hiking-outing-detail">
      <p className="hiking-outing-detail__eyebrow">
        <Link to={hikingPath(`/clubs/${outing.clubId}`)}>Back to club</Link>
      </p>
      <header className="hiking-outing-detail__header">
        <h1>{outing.title}</h1>
        <p className="hiking-outing-detail__when">{formatScheduleDateTime(outing.outingDate)}</p>
        <p className="hiking-outing-detail__meet">
          Meeting: <strong>{outing.meetingLocation}</strong>
        </p>
        {outing.meetingNotes && <p className="hiking-outing-detail__notes">{outing.meetingNotes}</p>}
        {outing.pace && <p>Pace: {formatOutingPace(outing.pace)}</p>}
        {(outing.resolvedDurationMinutes ?? outing.expectedDurationMinutes) != null && (
          <p>
            Planned time on trail:{' '}
            {formatDuration(outing.resolvedDurationMinutes ?? outing.expectedDurationMinutes)}
          </p>
        )}
        {outing.description && <p>{outing.description}</p>}
        {outing.status !== 'SCHEDULED' && (
          <p className="hiking-outing-detail__status">Status: {outing.status}</p>
        )}
      </header>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {outing.trail && (
        <section className="hiking-outing-detail__section">
          <h2>Selected trail</h2>
          <div className="hiking-outing-detail__trail">
            <h3>{outing.trail.title}</h3>
            {outing.trail.parkName && <p>{outing.trail.parkName}</p>}
            {outing.trail.distanceKm != null && <p>{formatDistance(outing.trail.distanceKm)}</p>}
            {outing.trail.elevationGainM != null && (
              <p>{formatElevation(outing.trail.elevationGainM)}</p>
            )}
            <p>
              {[
                outing.trail.difficulty && formatTrailDifficulty(outing.trail.difficulty),
                outing.trail.terrain && formatTrailTerrain(outing.trail.terrain),
                outing.trail.routeShape && formatTrailRouteShape(outing.trail.routeShape),
              ]
                .filter(Boolean)
                .join(' · ')}
            </p>
            {outing.trail.trailheadLocation && (
              <p>Trailhead: {outing.trail.trailheadLocation}</p>
            )}
            {outing.trail.parkingNotes && <p>Parking: {outing.trail.parkingNotes}</p>}
            {outing.trail.mapUrl && (
              <a href={outing.trail.mapUrl} target="_blank" rel="noreferrer">
                Open map
              </a>
            )}
            <PhotoGallery photos={outing.trail.photoUrls || []} />
          </div>
        </section>
      )}

      {(outing.packList || []).length > 0 && (
        <section className="hiking-outing-detail__section">
          <h2>Pack list</h2>
          <ul className="hiking-outing-detail__pack-list">
            {outing.packList.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>
      )}

      <section className="hiking-outing-detail__section">
        <h2>Who is hiking</h2>
        <div className="hiking-outing-detail__counts">
          <span>Going {counts.AVAILABLE}</span>
          <span>Maybe {counts.MAYBE}</span>
          <span>Reserve {counts.RESERVE}</span>
          <span>Out {counts.UNAVAILABLE}</span>
          {outing.hikerCapacity != null && <span>Capacity {outing.hikerCapacity}</span>}
        </div>

        {isAuthenticated ? (
          <form className="hiking-outing-detail__vote" onSubmit={handleVote}>
            <div className="hiking-outing-detail__status-grid">
              {STATUS_OPTIONS.map((opt) => (
                <label key={opt.value} className={myStatus === opt.value ? 'is-active' : ''}>
                  <input
                    type="radio"
                    name="status"
                    value={opt.value}
                    checked={myStatus === opt.value}
                    onChange={() => setMyStatus(opt.value)}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
            <label>
              Notes
              <input value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={200} />
            </label>
            {!hiker && (
              <p className="hiking-outing-detail__hint">
                <Link to={hikingCompleteProfilePath()}>Complete your hiking profile</Link> to vote.
              </p>
            )}
            <button type="submit" className="btn btn-primary" disabled={voting || !hiker}>
              {voting ? 'Saving...' : 'Save availability'}
            </button>
          </form>
        ) : (
          <p>
            <Link to="/login">Log in</Link> to vote on this outing.
          </p>
        )}

        <ul className="hiking-outing-detail__votes">
          {votes.map((vote) => (
            <li key={vote.id}>
              <strong>{vote.player?.name || 'Hiker'}</strong>
              <span>{vote.status}</span>
              {vote.notes && <em>{vote.notes}</em>}
            </li>
          ))}
        </ul>
      </section>

      <section className="hiking-outing-detail__section">
        <h2>Share with the group</h2>
        <p>Outing details, trail, meeting spot, pack list, and photo gallery are included.</p>
        <div className="hiking-outing-detail__share-actions">
          {canManage && (
            <button type="button" className="btn btn-primary" onClick={handleShare}>
              {shareUrl ? 'Refresh share link' : 'Create share link'}
            </button>
          )}
          {shareUrl && (
            <>
              <button type="button" className="btn btn-secondary" onClick={copyShare}>
                Copy link
              </button>
              {whatsappHref && (
                <a className="btn btn-secondary" href={whatsappHref} target="_blank" rel="noreferrer">
                  Share on WhatsApp
                </a>
              )}
            </>
          )}
        </div>
        {shareUrl && (
          <p className="hiking-outing-detail__share-url">
            <a href={shareUrl}>{shareUrl}</a>
          </p>
        )}
      </section>

      <section className="hiking-outing-detail__section">
        <div className="hiking-outing-detail__photos-head">
          <h2>Trail photos</h2>
          {isAuthenticated && (
            <label className="btn btn-secondary hiking-outing-detail__file">
              Upload photos
              <input
                type="file"
                accept="image/*"
                multiple
                hidden
                onChange={(e) => handlePhotos(e.target.files)}
              />
            </label>
          )}
        </div>
        <PhotoGallery photos={outing.photoUrls || []} />
        {canManage && (outing.photoUrls || []).length > 0 && (
          <ul className="hiking-outing-detail__photo-admin">
            {outing.photoUrls.map((url) => (
              <li key={url}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => handleDeletePhoto(url)}
                >
                  Remove photo
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};

export default HikingOutingDetail;
